import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/var/www/html/src/components/Layout/Tutorials.tsx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Building automated voice surveys with MessageBird`}</h1>
    <h3>{`⏱ 15 min build time || `}<a parentName="h3" {...{
        "href": "https://github.com/messagebirdguides/automated-surveys-java"
      }}>{`Download the Code`}</a></h3>
    <h2>{`Why build automated voice surveys?`}</h2>
    <p>{`In this MessageBird Developer Tutorial, you’ll learn how to gather valuable information from your users with this fully automated voice survey application powered by the `}<a parentName="p" {...{
        "href": "/api/voice-calling"
      }}>{`MessageBird Voice Calling API`}</a>{`.`}</p>
    <p>{`Surveys are a great way to gather feedback about a product or service. Here we'll look at a company that wants to collect surveys over the phone by providing their customers a feedback number that they can call, submit their opinion as voice messages that the company's support team can listen to on a website, and incorporate that feedback into the next version of the product. This team should be able to focus their attention on the input instead of having to wait and answer calls; therefore, the feedback collection itself is fully automated.`}</p>
    <h2>{`Getting started`}</h2>
    <p>{`First things first, our sample application is built in Java with the `}<a parentName="p" {...{
        "href": "http://sparkjava.com/"
      }}>{`Spark framework`}</a>{` and `}<a parentName="p" {...{
        "href": "http://sparkjava.com/documentation#views-and-templates"
      }}>{`Handlebars templates`}</a>{`.`}</p>
    <p>{`You need to install `}<a parentName="p" {...{
        "href": "https://www.oracle.com/technetwork/java/javase/downloads/jdk8-downloads-2133151.html"
      }}>{`Java 1.8`}</a>{` and `}<a parentName="p" {...{
        "href": "https://maven.apache.org/"
      }}>{`Maven`}</a>{`. The source code is available in the `}<a parentName="p" {...{
        "href": "https://github.com/messagebirdguides/automated-surveys-java"
      }}>{`MessageBird Developer Tutorials GitHub repository`}</a>{`, which you can either clone with git or from where you can download a ZIP file with the source code to your computer.`}</p>
    <p>{`The sample application uses `}<a parentName="p" {...{
        "href": "https://mongodb.github.io/mongo-java-driver/"
      }}>{`MongoDB`}</a>{` to provide an in-memory database for testing, so you don't need to configure an external database. As the mock loses data when you restart the application you need to replace it with a real server when you want to develop this sample into a production application.`}</p>
    <h2>{`Designing the call flow`}</h2>
    <p>{`Call flows in MessageBird are sequences of steps. Each step can be a different action, such as playing an audio file, speaking words through text-to-speech (TTS), recording the caller's voice or transferring the call to another party. The call flow for this survey application alternates two types of actions: saying the question (`}<inlineCode parentName="p">{`say`}</inlineCode>{` action) and recording an answer (`}<inlineCode parentName="p">{`record`}</inlineCode>{` action). Other action types are not required. The whole flow begins with a short introduction text and ends on a "Thank you" note, both of which are implemented as `}<inlineCode parentName="p">{`say`}</inlineCode>{` actions.`}</p>
    <p>{`The survey application generates the call flow dynamically through Javascript code and provides it on a webhook endpoint as a JSON response that MessageBird can parse; however, it doesn’t return the complete flow at once. The generated steps always end on a `}<inlineCode parentName="p">{`record`}</inlineCode>{` action with the `}<inlineCode parentName="p">{`onFinish`}</inlineCode>{` attribute set to the same webhook endpoint URL. This approach simplifies the collection of recordings because whenever the caller provides an answer, an identifier for the recording is sent with the next webhook request. The endpoint will then store information about the answer to the question and return additional steps: either the next question together with its answer recording step or, if the caller has reached the end of the survey, the final “Thank you” note.
The sample implementation contains only a single survey. For each participant, we create a (mocked) database entry that includes a unique MessageBird-generated identifier for the call, their number, and an array of responses. As the webhook is requested multiple times for each caller, once in the beginning and once for each answer they record, the length of the responses array indicates their position within the survey and determines the next step.`}</p>
    <p>{`All questions are stored as an array in the file `}<inlineCode parentName="p">{`questions.json`}</inlineCode>{` to keep them separate from the implementation.`}</p>
    <h2>{`Prerequisites for receiving calls`}</h2>
    <h3>{`Overview`}</h3>
    <p>{`Participants take part in a survey by calling a dedicated virtual phone number. MessageBird accepts the call and contacts the application on a `}<em parentName="p">{`webhook URL`}</em>{`, which you assign to your number on the MessageBird Dashboard using `}<a parentName="p" {...{
        "href": "https://dashboard.messagebird.com/en/flow-builder"
      }}>{`Flow Builder`}</a>{`. A webhook is a URL on your site that doesn't render a page to users but is like an API endpoint that can be triggered by other servers. Every time someone calls that number, MessageBird checks that URL for instructions on how to interact with the caller.`}</p>
    <h3>{`Exposing your development server with ngrok`}</h3>
    <p>{`When working with webhooks, an external service like MessageBird needs to access your application, so the URL must be public; however, during development you're typically working in a local development environment that is not publicly available. Thankfully this is not a big deal since various tools and services allow you to quickly expose your development environment to the Internet by providing a tunnel from a public URL to your local machine. One of the most popular tools is `}<a parentName="p" {...{
        "href": "https://ngrok.com/"
      }}>{`ngrok`}</a>{`.`}</p>
    <p>{`You can download `}<a parentName="p" {...{
        "href": "https://ngrok.com/download"
      }}>{`ngrok`}</a>{` for free as a single-file binary for almost every operating system, or optionally sign up for an account to access additional features.`}</p>
    <p>{`Let’s start a tunnel by providing a local port number on which your application runs. We’ll run our Java server on port 4567, so you can launch your tunnel with this command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ngrok http 4567
`}</code></pre>
    <p>{`After you've launched the tunnel, ngrok displays your temporary public URL along with some other information. We'll need that URL in a minute.`}</p>
    <p>{`Another common tool for tunneling your local machine is `}<a parentName="p" {...{
        "href": "https://localtunnel.me/"
      }}>{`localtunnel.me`}</a>{`, which works virtually in the same way; you can have a look at it if you're facing problems with with ngrok. Keep in mind that it requires you to install `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/"
      }}>{`NPM`}</a>{` first.`}</p>
    <h3>{`Getting an inbound number`}</h3>
    <p>{`A requirement for receiving messages is a dedicated inbound number. Virtual mobile numbers (VNM) look and work similar to regular mobile numbers; however, instead of being attached to a mobile device via a SIM card, they live in the cloud and can process inbound SMS and voice calls. MessageBird offers numbers from different countries for a low monthly fee; `}<a parentName="p" {...{
        "href": "https://www.messagebird.com/en/numbers"
      }}>{`feel free to explore our low-cost programmable and configurable numbers`}</a>{`.`}</p>
    <p>{`Purchasing a number is quite easy:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Go to the ‘`}<a parentName="p" {...{
            "href": "https://dashboard.messagebird.com/en/numbers"
          }}>{`Numbers`}</a>{`’ section in the left-hand side of your Dashboard and click the blue button ‘`}<a parentName="p" {...{
            "href": "https://dashboard.messagebird.com/en/vmn/buy-number"
          }}>{`Buy a number`}</a>{`’ in the top-right side of your screen.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Pick the country in which you and your customers are located, and make sure the Voice capability is selected.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Choose one number from the selection and the duration for which you want to pay now.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Confirm by clicking ‘Buy Number’ in the bottom-right of your screen.`}</p>
      </li>
    </ol>
    <p><img parentName="p" {...{
        "src": "/img/screenshots/automated-surveys-java/image1.png",
        "alt": "VMN"
      }}></img></p>
    <p>{`Awesome, you’ve set up your first virtual mobile number! 🎉`}</p>
    <p><strong parentName="p">{`Pro-tip:`}</strong>{` Check out our Help Center for more information about `}<a parentName="p" {...{
        "href": "https://support.messagebird.com/hc/en-us/sections/201958489-Virtual-Numbers"
      }}>{`virtual mobile numbers`}</a>{` and `}<a parentName="p" {...{
        "href": "https://support.messagebird.com/hc/en-us/sections/360000108538-Country-info-Restrictions"
      }}>{`country restrictions`}</a>{`.`}</p>
    <h3>{`Connecting the number to your application`}</h3>
    <p>{`So you have a number now, but MessageBird has no idea what to do with it. That's why now you need to define a `}<em parentName="p">{`Flow`}</em>{` that links your number to your webhook.`}</p>
    <h4>{`STEP ONE`}</h4>
    <p>{`Go back to `}<a parentName="p" {...{
        "href": "https://dashboard.messagebird.com/en/flow-builder"
      }}>{`Flow Builder`}</a>{` and hit the button ‘Create new flow’ and then ‘Create Custom Flow’.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/screenshots/automated-surveys-java/image2.png",
        "alt": "FlowBuilder"
      }}></img></p>
    <h4>{`STEP TWO`}</h4>
    <p>{`Give your flow a name, choose ‘Phone Call’ as the trigger and hit ‘Next’.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/screenshots/automated-surveys-java/image3.png",
        "alt": "FlowBuilder"
      }}></img></p>
    <h4>{`STEP THREE`}</h4>
    <p>{`Click on the first step ‘Phone Call’ and select the number or numbers you’d like to attach the flow to.`}</p>
    <h4>{`STEP FOUR`}</h4>
    <p>{`Add a new step by pressing the small ‘+’, choose ‘Fetch call flow from URL’ and paste the localtunnel base URL into the form and append `}<inlineCode parentName="p">{`/callStep`}</inlineCode>{` to it-this is the name of our webhook handler route. Click on ‘Save’ when ready.`}</p>
    <p><img parentName="p" {...{
        "src": "/img/screenshots/automated-surveys-java/image4.png",
        "alt": "FlowBuilder"
      }}></img></p>
    <h4>{`STEP FIVE`}</h4>
    <p><strong parentName="p">{`Ready!`}</strong>{` Hit ‘Publish’ on the right top of the screen to activate your flow. Your flow should look something like this:`}</p>
    <p><img parentName="p" {...{
        "src": "/img/screenshots/automated-surveys-java/image5.png",
        "alt": "FlowBuilder"
      }}></img></p>
    <h2>{`Implementing the call steps`}</h2>
    <p>{`The routes `}<inlineCode parentName="p">{`get /callStep`}</inlineCode>{` and `}<inlineCode parentName="p">{`post /callStep`}</inlineCode>{` in `}<inlineCode parentName="p">{`AutomatedVoiceSurvey.json`}</inlineCode>{` contains the implementation of the survey call flow:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`get("/callStep", (req, res) -> handleCallStep(req, res, mongoClient, questions));
post("/callStep", (req, res) -> handleCallStep(req, res, mongoClient, questions));

`}</code></pre>
    <p><inlineCode parentName="p">{`handleCallStep`}</inlineCode>{` starts with the basic structure for a hash object called `}<inlineCode parentName="p">{`flow`}</inlineCode>{`, which we'll extend depending on where we are within our survey:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`// Prepare a Call Flow that can be extended
JSONObject flow = new JSONObject();
JSONArray steps = new JSONArray();
`}</code></pre>
    <p>{`Next, we connect to MongoDB, select a collection, and try to find an existing call:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`MongoDatabase database = mongoClient.getDatabase("myproject");
MongoCollection<Document> collection = database.getCollection("survey_participants");

String callID = req.queryParams("callID");
Document doc = collection.find(eq("callId", callID)).first();
`}</code></pre>
    <p>{`The application continues inside the callback function. First, we determine the ID (array index) of the next question, which is 0 for new participants or the number of existing answers plus one for existing ones:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`// Determine the next question
Integer questionID = 0;

if (doc != null) {
   List<Document> responses = (List<Document>) doc.get("responses");
   questionID = responses.size() + 1;
}
`}</code></pre>
    <p>{`For new participants, we also need to create a document in the MongoDB collection and persist it to the database. This record contains the identifier of the call and the caller ID, which are taken from the query parameters sent by MessageBird as part of the webhook (call flow fetch) request, `}<inlineCode parentName="p">{`callID`}</inlineCode>{`, and `}<inlineCode parentName="p">{`destination`}</inlineCode>{` respectively. It includes an empty responses array as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`else {
   // Create new participant database entry
   Document entry = new Document("callId", req.queryParams("callID"))
           .append("number", req.queryParams("destination"))
           .append("responses", new JSONArray());
   collection.insertOne(entry);
}
`}</code></pre>
    <p>{`Let's move back to our route's implementation. The answers are persisted by adding them to the responses array and then updating the document in the MongoDB collection. For every answer we store two identifiers from the parsed JSON request body: the `}<inlineCode parentName="p">{`legId`}</inlineCode>{` that identifies the caller in a multi-party voice call and is required to fetch the recording, as well as the id of the recording itself which we store as `}<inlineCode parentName="p">{`recordingId`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`if (questionID > 0) {
   JSONParser parser = new JSONParser();
   JSONObject requestPayload;
   try {
       requestPayload = (JSONObject) parser.parse(req.body());
       // Unless we're at the first question, store the response
       // of the previous question
       Document response = new Document().append("legId", requestPayload.get("legId"))
               .append("recordingId", requestPayload.get("id"));

       collection.updateOne(eq("callId", req.queryParams("callID")), Updates.addToSet("responses", response));
   } catch (ParseException e) {
       e.printStackTrace();
   }
}
`}</code></pre>
    <p>{`It's time to ask a question. Let's first check if we reached the end of the survey. That is determined by whether the question index equals the length of the questions list and therefore is out of bounds of the array, which means there are no further questions; if so, we thank the caller for their participation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`if (questionID == questions.size()) {
   // All questions have been answered
   steps.add(say("You have completed our survey. Thank you for participating!"));
}
`}</code></pre>
    <p>{`Did you notice the `}<inlineCode parentName="p">{`say()`}</inlineCode>{` function? It’s a small helper function we've declared separately in the initial section of `}<inlineCode parentName="p">{`AutomatedVoiceSurvey.java`}</inlineCode>{` to simplify the creation of `}<inlineCode parentName="p">{`say`}</inlineCode>{` steps as we need them multiple times in the application. The `}<inlineCode parentName="p">{`say()`}</inlineCode>{` function returns the action in the format expected by MessageBird so it can be added to the steps of a flow using `}<inlineCode parentName="p">{`push()`}</inlineCode>{`, as seen above. A function like this allows setting options for `}<inlineCode parentName="p">{`say`}</inlineCode>{` actions at a central location. You can modify it if you want to, for example, specify another language or voice:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`public static JSONObject say(String payload) {
   JSONObject result = new JSONObject();
   JSONObject options = new JSONObject();
   options.put("payload", payload);
   options.put("voice", "male");
   options.put("language", "en-US");
   result.put("action", "say");
   result.put("options", options);

   return result;
}
`}</code></pre>
    <p>{`Back in the route, there's an `}<inlineCode parentName="p">{`else`}</inlineCode>{`-block that handles all questions other than the last; however, there's another nested `}<inlineCode parentName="p">{`if`}</inlineCode>{`-statement in it to treat the first question, since we need to read a welcome message to our participant `}<em parentName="p">{`before`}</em>{` the question:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`if (questionID == 0) {
   // Before first question, say welcome
   steps.add(say(String.format("Welcome to our survey! You will be asked %s questions. The answers will be recorded. Speak your response for each and press any key on your phone to move on to the next question. Here is the first question:", questions.size())));
}
`}</code></pre>
    <p>{`Finally, here comes the general logic used for each question:`}</p>
    <ul>
      <li parentName="ul">{`Ask the question using `}<inlineCode parentName="li">{`say`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Request a recording.`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`// Ask next question
steps.add(say(questions.get(questionID).toString()));

// Request recording of question
JSONObject action = new JSONObject();
JSONObject options = new JSONObject();
options.put("finishOnKey", "any"); // Finish either on key press or after 10 seconds of silence
options.put("timeout", 10);
options.put("onFinish", String.format("http://%s/callStep", req.host())); // Send recording to this same call flow URL
action.put("action", "record");
action.put("options", options);
steps.add(action);
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`record`}</inlineCode>{` step is configured so that it finishes when the caller presses any key on their phone's keypad (`}<inlineCode parentName="p">{`finishOnKey`}</inlineCode>{` attribute) or when MessageBird detects 10 seconds of silence (`}<inlineCode parentName="p">{`timeout`}</inlineCode>{` attribute). By specifying the URL with the `}<inlineCode parentName="p">{`onFinish`}</inlineCode>{` attribute, we can make sure that the recording data is sent back to our route and that we can send additional steps to the caller. Building the URL with protocol and hostname information from the request ensures that it works wherever the application is deployed and also behind the tunnel.`}</p>
    <p>{`Only one tiny part remains: the last step in each webhook request is sending back a JSON response based on the `}<inlineCode parentName="p">{`flow`}</inlineCode>{` object:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`res.type("application/json");
return flow;
`}</code></pre>
    <h2>{`Building an admin view`}</h2>
    <p>{`The survey application also contains an admin view that allows us to view the survey participants and listen to their responses. The implementation of the `}<inlineCode parentName="p">{`get '/admin'`}</inlineCode>{` route is straightforward: it essentially loads everything from the database plus the questions data and adds it to the data available for a `}<a parentName="p" {...{
        "href": "http://sparkjava.com/documentation#views-and-templates"
      }}>{`Handlebars templates`}</a>{`.`}</p>
    <p>{`The template, which you can see in `}<inlineCode parentName="p">{`resources/templates/participants.handlebars`}</inlineCode>{`, contains a basic HTML structure with a three-column table. Inside the table, two nested loops over the participants and their responses add a line for each answer with the number of the caller, the question and a "Listen" button that plays it back.`}</p>
    <p>{`Let's have a more detailed look at the implementation of this "Listen" button. On the frontend, the button calls a Javascript function called `}<inlineCode parentName="p">{`playAudio()`}</inlineCode>{` with the `}<inlineCode parentName="p">{`callId`}</inlineCode>{`, `}<inlineCode parentName="p">{`legId`}</inlineCode>{`, and `}<inlineCode parentName="p">{`recordingId`}</inlineCode>{` inserted through ERB expressions:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-HTML"
      }}>{`<button onclick="playAudio('{{p.callId}}','{{this.legId}}','{{this.recordingId}}')">Listen</button>
`}</code></pre>
    <p>{`The implementation of that function dynamically generates an invisible, auto-playing HTML5 audio element:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function playAudio(callId, legId, recordingId) {
  document.getElementById('audioplayer').innerHTML =
    '<audio autoplay="1"><source src="/play/' +
    callId +
    '/' +
    legId +
    '/' +
    recordingId +
    '" type="audio/wav"></audio>';
}
`}</code></pre>
    <p>{`As you can see, the WAV audio is requested from a route of the survey application. This route acts as a proxy server that fetches the audio from the MessageBird API and forward it to the frontend. This architecture is necessary because we need a MessageBird API key to fetch the audio, but don't want to expose it on the client-side of our application. We use request to make the API call and add the API key as an HTTP header:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-java"
      }}>{`get("/play/*/*/*",
       (req, res) ->
       {
           File temp = File.createTempFile("temp-file", ".wav");

           URL url = new URL(String.format("https://voice.messagebird.com/calls/%s/legs/%s/recordings/%s.wav", req.splat()[0], req.splat()[1], req.splat()[2]));
           HttpURLConnection http = (HttpURLConnection) url.openConnection();

           byte[] wavFile = Files.readAllBytes(Paths.get(temp.getAbsolutePath()));

           http.setRequestMethod("POST");
           http.setRequestProperty("Authorization", String.format("AccessKey %s", dotenv.get("MESSAGEBIRD_API_KEY")));
           http.setRequestProperty("Content-Type", "audio/wav");
           http.setDoOutput(true);

           DataOutputStream request = new DataOutputStream(http.getOutputStream());
           request.write(wavFile);
           request.flush();
           request.close();

           res.type("*/*");
           res.raw().setContentLength(wavFile.length);

           res.status(200);

           final ServletOutputStream os = res.raw().getOutputStream();
           final ByteArrayInputStream in = new ByteArrayInputStream(wavFile);
           IOUtils.copy(in, os);
           in.close();
           os.close();

           return null;
       }
);
`}</code></pre>
    <p>{`You need to provide a MessageBird API key via an environment variable loaded, let’s do it by using `}<a parentName="p" {...{
        "href": "https://mvnrepository.com/artifact/io.github.cdimascio/java-dotenv"
      }}>{`dotenv`}</a>{`. We've prepared an `}<inlineCode parentName="p">{`env.example`}</inlineCode>{` file in the repository, which you should rename to `}<inlineCode parentName="p">{`.env`}</inlineCode>{` and add the required information. Here's an example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`MESSAGEBIRD_API_KEY=YOUR-API-KEY
`}</code></pre>
    <p>{`Go to the `}<a parentName="p" {...{
        "href": "https://dashboard.messagebird.com/en/user/index"
      }}>{`MessageBird Dashboard`}</a>{`; if you have already created an API key it will be shown right there. If you don’t see any key on the Dashboard or if you're unsure whether this key is in `}<em parentName="p">{`live`}</em>{` mode, go to the `}<em parentName="p">{`Developers`}</em>{` section in the MessageBird Dashboard and open the `}<a parentName="p" {...{
        "href": "https://dashboard.messagebird.com/en/developers/access"
      }}>{`API access (REST) tab`}</a>{`. There you can create new API keys and manage your existing ones.`}</p>
    <h2>{`Testing`}</h2>
    <p>{`You’re done! It’s time to test your application.`}</p>
    <p>{`Double-check that you’ve set up your number correctly with a flow to forward incoming phone calls to an ngrok URL and that the tunnel is still running. Keep in mind that whenever you start a fresh tunnel with the ngrok command, you'll get a new URL, so you have to update it in the flow accordingly.`}</p>
    <p>{`To start the application, build and run the application through your IDE.`}</p>
    <p>{`Now, take your phone and dial your survey number. You should hear the welcome message and the first question; speak an answer and press any key, at that moment you should see some database debug output in the console. Open http://localhost:4567/admin to see your call as well. Continue interacting with the survey. In the end, you can refresh your browser and listen to all the answers you recorded within your phone call.`}</p>
    <p>{`Congratulations, you just deployed a survey system with MessageBird!`}</p>
    <h2>{`Supporting outbound calls`}</h2>
    <p>{`The application was designed for incoming calls where survey participants call a virtual number and can provide their answers; the same code works without any changes for an outbound call scenario as well. All you have to do is start a call through the API or other means and use a call flow that contains a `}<inlineCode parentName="p">{`fetchCallFlow`}</inlineCode>{` step pointing to your webhook route.`}</p>
    <p>{`Use the flow, code snippets, and UI examples from this tutorial to build your own automated voice survey. Don't forget to download the code from the `}<a parentName="p" {...{
        "href": "https://github.com/messagebirdguides/automated-surveys"
      }}>{`MessageBird Developer Tutorials GitHub repository`}</a>{`.`}</p>
    <h3>{`Nice work! 🎉`}</h3>
    <p>{`You now have a running integration of MessageBird's Voice API using Java!`}</p>
    <h2>{`Start building!`}</h2>
    <p>{`Want to build something similar but not quite sure how to get started? Please feel free to let us know at `}<a parentName="p" {...{
        "href": "mailto:support@messagebird.com"
      }}>{`support@messagebird.com`}</a>{`; we'd love to help!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      